import { IconList } from '../../ui/heroicons-picker/iconType';

/**
 * AgentAppOverrides mirrors the backend's AgentAppOverrides Pydantic model.
 * Every field is optional — missing/`null` means "inherit from website".
 * Stored at `agent.config.app` server-side.
 */
export interface AgentAppOverrides {
    // Prompting
    goal?: string | null;

    // Starters (full replacement when set)
    starters?: Array<{ id: string; value: string }> | null;
    links?: Array<{ id: string; name: string; description: string; url: string }> | null;

    // Popup / engagement
    welcomeMessage?: string | null;
    searchBarPlaceholder?: string | null;
    popupMessage?: string | null;
    popupMessageEnabled?: boolean | null;
    contextualPopupEnabled?: boolean | null;
    autoOpenEnabled?: boolean | null;
    autoOpenSeconds?: number | null;
    openOncePerSession?: boolean | null;

    // Appearance
    color?: string | null;
    endColor?: string | null;
    mixedColor?: string | null;
    darkerColor?: string | null;
    lighterColor?: string | null;
    scheme?: string | null;
    font?: string | null;
    widgetPosition?: string | null;
    collapsedMode?: 'chat' | 'copilot' | null;
    expandedMode?: 'chat' | 'copilot' | 'sidebar' | null;
    hideAimdoc?: boolean | null;
    agentMessageIconType?: string | null;
    agentMessageIcon?: IconList | null;
    darkModeEnabled?: boolean | null;
    darkModeDefault?: 'system' | 'light' | 'dark' | null;
    darkModeColor?: string | null;
}

export type AppOverrideKey = keyof AgentAppOverrides;

/** Normalize a raw object (possibly from the API) into a typed AgentAppOverrides. */
export function normalizeAppOverrides(raw: unknown): AgentAppOverrides {
    if (!raw || typeof raw !== 'object') return {};
    const result: AgentAppOverrides = {};
    const src = raw as Record<string, unknown>;
    for (const key of Object.keys(src) as AppOverrideKey[]) {
        const value = src[key];
        if (value === undefined || value === null) continue;
        (result as any)[key] = value;
    }
    return result;
}

/** Strip null/undefined keys so we only persist real overrides. */
export function pruneAppOverrides(overrides: AgentAppOverrides): AgentAppOverrides {
    const result: AgentAppOverrides = {};
    for (const key of Object.keys(overrides) as AppOverrideKey[]) {
        const value = overrides[key];
        if (value === undefined || value === null) continue;
        (result as any)[key] = value;
    }
    return result;
}

/** True if any override key is set (i.e. any field diverges from website). */
export function hasAnyOverrides(overrides: AgentAppOverrides): boolean {
    return Object.keys(pruneAppOverrides(overrides)).length > 0;
}

export function countOverrides(
    overrides: AgentAppOverrides,
    keys: AppOverrideKey[],
): number {
    let count = 0;
    for (const key of keys) {
        const value = overrides[key];
        if (value !== undefined && value !== null) count += 1;
    }
    return count;
}

/** Override-key groupings used by sidebar badges on the In-app side. */
export const PROMPTING_OVERRIDE_KEYS: AppOverrideKey[] = ['goal'];

export const CUSTOMIZATION_OVERRIDE_KEYS: AppOverrideKey[] = [
    'starters',
    'links',
    'welcomeMessage',
    'searchBarPlaceholder',
    'popupMessage',
    'popupMessageEnabled',
    'contextualPopupEnabled',
    'autoOpenEnabled',
    'autoOpenSeconds',
    'openOncePerSession',
];

export const APPEARANCE_OVERRIDE_KEYS: AppOverrideKey[] = [
    'color',
    'endColor',
    'mixedColor',
    'darkerColor',
    'lighterColor',
    'scheme',
    'font',
    'widgetPosition',
    'expandedMode',
    // `collapsedMode` is intentionally excluded: in-app the collapsed state is
    // always the command-bar pill (see AppAppearanceTab).
    'hideAimdoc',
    'agentMessageIconType',
    'agentMessageIcon',
    'darkModeEnabled',
    'darkModeDefault',
    'darkModeColor',
];
